import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import {
  systemPerformanceLiveQuery,
  systemPerformanceQuery,
  systemPerformanceWatch,
  type DeviceSystemPerformancePage,
  type ProactiviteeLivePayload,
  type ProactiviteePerfTile,
  type SystemPerformanceHdd,
  type SystemPerformanceSensor,
} from '../api/devices'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila System Performance - hardwareMonitoring.php (include performanceStatus.php):
 * placutele Live performance, cartoanele CPU / Storage, tabelele Voltages / Fans.
 */

const BOTTLENECK: Record<string, string> = {
  cpu: 'CPU',
  ram: 'RAM',
  disk: 'Disk',
  gpu: 'GPU',
  none: 'Balanced',
}

function tileTone(
  metric: string | null,
  value: number | null,
  thresholds: Record<string, number>,
  pairs: { metric: string; warn: string; crit: string }[],
): string {
  if (metric === null || value === null || Number.isNaN(value)) return 'blue-madison'
  const pair = pairs.find((p) => p.metric === metric)
  if (!pair) return 'blue-madison'
  if (value >= thresholds[pair.crit]!) return 'red-intense'
  if (value >= thresholds[pair.warn]!) return 'yellow'
  return 'blue-madison'
}

function shortDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—'
  if (seconds < 1) return '0s'
  const units: [string, number][] = [
    ['z', 86400],
    ['h', 3600],
    ['m', 60],
    ['s', 1],
  ]
  for (let i = 0; i < units.length; i++) {
    const [label, size] = units[i]!
    const value = Math.floor(seconds / size)
    if (value <= 0) continue
    let out = `${value}${label}`
    const next = units[i + 1]
    if (next) {
      const rest = Math.floor((seconds - value * size) / next[1])
      if (rest > 0) out += ` ${rest}${next[0]}`
    }
    return out
  }
  return '0s'
}

function applyLive(
  tiles: ProactiviteePerfTile[],
  live: ProactiviteeLivePayload,
  thresholds: Record<string, number>,
  pairs: { metric: string; warn: string; crit: string }[],
): ProactiviteePerfTile[] {
  if (!live.has) return tiles
  const score = Math.round(live.score || 0)
  const scoreTone = score < 50 ? 'red-intense' : score < 80 ? 'yellow' : 'green-haze'
  const bn = BOTTLENECK[(live.bottleneck || '').toLowerCase()] || 'n/a'
  const cpuUtil = Number(live.cpu_util || 0)
  const ramUsed = Number(live.ram_used_percent || 0)
  const diskUtil = Number(live.disk_util || 0)
  const ioWait = Number(live.cpu_iowait || 0)
  const swapPct = Number(live.ram_swap_percent || 0)
  const diskAwait = Number(live.disk_await_ms || 0)
  const load1 = Number(live.cpu_load1 || 0)
  const cpuCount = Number(live.cpu_count || 0)
  const perCore = cpuCount > 0 ? load1 / cpuCount : 0
  const netTotal = live.net_connections || 0
  const netIn = live.net_in || 0
  const netOut = live.net_out || 0
  const apps = live.running_applications
  const gpu =
    live.gpu_util !== null && live.gpu_util !== '' && live.gpu_util !== undefined
      ? `${Number(live.gpu_util).toFixed(1)}%`
      : null
  const ring = (percent: number) => {
    const length = 2 * Math.PI * 26
    return { ring_length: Math.round(length * 100) / 100, ring_offset: Math.round(length * (1 - Math.max(0, Math.min(100, percent)) / 100) * 100) / 100 }
  }
  return tiles.map((tile) => {
    switch (tile.id) {
      case 'live-perf-headroom':
        return { ...tile, tone: scoreTone, number: String(score), more: `Bottleneck: ${bn}`, ...ring(score) }
      case 'live-perf-cpu-util':
        return {
          ...tile,
          tone: tileTone('load_per_core', perCore, thresholds, pairs),
          number: `${cpuUtil.toFixed(1)}%`,
          more: `Load ${load1.toFixed(2)} / ${cpuCount}`,
          metric_value: perCore,
          ...ring(cpuUtil),
        }
      case 'live-perf-ram-used':
        return {
          ...tile,
          tone: tileTone('ram_used', ramUsed, thresholds, pairs),
          number: `${ramUsed.toFixed(1)}%`,
          more: `${live.ram_available || ''} available`,
          metric_value: ramUsed,
          ...ring(ramUsed),
        }
      case 'live-perf-disk-util':
        return {
          ...tile,
          tone: tileTone('disk_await_ms', diskAwait, thresholds, pairs),
          number: `${diskUtil.toFixed(1)}%`,
          more: `${diskAwait.toFixed(1)} ms await`,
          metric_value: diskAwait,
          ...ring(diskUtil),
        }
      case 'live-perf-net-total':
        return { ...tile, number: String(netTotal), more: `${netIn} in · ${netOut} out` }
      case 'live-perf-apps':
        return apps ? { ...tile, number: String(apps) } : tile
      case 'live-perf-uptime':
        return typeof live.uptime_seconds === 'number' ? { ...tile, number: shortDuration(live.uptime_seconds) } : tile
      case 'live-perf-cpu-iowait':
        return { ...tile, number: `${ioWait.toFixed(1)}%`, more: `Steal ${Number(live.cpu_steal || 0).toFixed(1)}%`, ...ring(ioWait) }
      case 'live-perf-ram-swap':
        return {
          ...tile,
          tone: tileTone('swap', swapPct, thresholds, pairs),
          number: `${swapPct.toFixed(1)}%`,
          more: `${live.ram_swap_used || 'none'} / ${live.ram_swap_total || 'none'}`,
          metric_value: swapPct,
          ...ring(swapPct),
        }
      case 'live-perf-disk-read':
        return { ...tile, number: live.disk_read_bps || tile.number, more: `Write ${live.disk_write_bps || ''}` }
      case 'live-perf-net-established':
        return { ...tile, number: String(live.net_established || 0), more: `Of ${netTotal} connections` }
      case 'live-perf-gpu':
        return gpu ? { ...tile, number: gpu } : tile
      default:
        return tile
    }
  })
}

function Ring({ tile }: { tile: ProactiviteePerfTile }) {
  if (tile.ring === null) {
    return <i className={`fas ${tile.icon || 'fa-circle'}`}></i>
  }
  return (
    <svg className="pm-ring" viewBox="0 0 64 64" data-ring={tile.ring}>
      <circle cx="32" cy="32" r="26" fill="none" stroke="rgba(255,255,255,.25)" strokeWidth="7" />
      <circle
        className="pm-ring-fg"
        cx="32"
        cy="32"
        r="26"
        fill="none"
        stroke="#fff"
        strokeWidth="7"
        strokeLinecap="round"
        transform="rotate(-90 32 32)"
        strokeDasharray={String(tile.ring_length ?? '')}
        strokeDashoffset={String(tile.ring_offset ?? '')}
      />
    </svg>
  )
}

function PerfTile({ tile }: { tile: ProactiviteePerfTile }) {
  return (
    <div className="col-md-2">
      <div
        className={`dashboard-stat ${tile.tone}`}
        {...(tile.metric ? { 'data-tile-metric': tile.metric, 'data-tile-value': String(tile.metric_value ?? '') } : {})}
      >
        <div className="visual">
          <Ring tile={tile} />
        </div>
        <div className="details">
          <div className={`number${tile.small ? ' live-perf-small' : ''}`} id={tile.id}>
            {tile.number}
          </div>
          <div className="desc">{tile.label}</div>
        </div>
        <div className="more" dangerouslySetInnerHTML={{ __html: tile.more }} />
      </div>
    </div>
  )
}

function LivePerformance({
  page,
  live,
}: {
  page: DeviceSystemPerformancePage
  live: ProactiviteeLivePayload | undefined
}) {
  const tiles = useMemo(() => {
    const base = page.performance?.tiles ?? []
    if (!live) return base
    return applyLive(base, live, page.thresholds, page.saturation_pairs)
  }, [live, page.performance, page.saturation_pairs, page.thresholds])
  if (!page.performance) return null
  return (
    <div className="row" id="live-performance-panel" data-device={page.device.id}>
      <div className="col-md-12 divider">
        <h4 className="bordered">Live performance</h4>
      </div>
      {tiles.map((tile) => (
        <PerfTile key={tile.id} tile={tile} />
      ))}
    </div>
  )
}

function CpuChart({ load }: { load: number }) {
  const length = 2 * Math.PI * 35
  const offset = length * (1 - Math.max(0, Math.min(100, load)) / 100)
  return (
    <span className="easychart cpu-chart small" data-percent={load}>
      <svg viewBox="0 0 80 80" width="80" height="80">
        <circle cx="40" cy="40" r="35" fill="none" stroke="#FFF" strokeWidth="5" />
        <circle
          cx="40"
          cy="40"
          r="35"
          fill="none"
          stroke="#FFAA00"
          strokeWidth="5"
          strokeLinecap="round"
          transform="rotate(-90 40 40)"
          strokeDasharray={String(length)}
          strokeDashoffset={String(offset)}
        />
      </svg>
      <span className="percent">{Math.round(load)}%</span>
    </span>
  )
}

function SmartMark({ smart }: { smart: SystemPerformanceHdd['smart'] }) {
  if (smart === 'unsupported') return <b className="color-yellow">Not supported</b>
  if (smart === 'ok') return <i className="fas fa-check-circle font-14 color-green2"></i>
  return <i className="fa faa-flash animated fa-exclamation-circle font-14 color-red"></i>
}

function WmiMark({ ok }: { ok: boolean }) {
  return ok ? (
    <i className="fas fa-check-circle font-14 color-green2"></i>
  ) : (
    <i className="fa faa-flash animated fa-exclamation-circle font-14 color-red"></i>
  )
}

function SensorTable({ heading, unit, rows }: { heading: string; unit: string; rows: SystemPerformanceSensor[] }) {
  return (
    <div className="panel panel-default">
      <div className="panel-heading">{heading}</div>
      <table className="table table-hover">
        <thead style={{ backgroundColor: '#FFF' }}>
          <tr>
            <th className="align-left">COMPONENT</th>
            <th className="align-left">CURRENT</th>
            <th className="align-left">MIN</th>
            <th className="align-left">MAX</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.component}-${row.current}`}>
              <td>
                <strong>{row.component}</strong>
              </td>
              <td>
                {row.current} {unit}
              </td>
              <td>
                {row.min} {unit}
              </td>
              <td>
                {row.max} {unit}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function DeviceSystemPerformance({ id }: ScreenProps) {
  const { data, error } = useQuery(systemPerformanceQuery(id))
  const live = useQuery({
    ...systemPerformanceLiveQuery(id),
    enabled: !!data,
  })
  useEffect(() => {
    if (!data) return
    void systemPerformanceWatch(id)
    const timer = window.setInterval(() => {
      void systemPerformanceWatch(id)
    }, 3000)
    return () => window.clearInterval(timer)
  }, [data, id])
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />
  return <Hardware page={data} live={live.data} />
}

function Hardware({ page, live }: { page: DeviceSystemPerformancePage; live: ProactiviteeLivePayload | undefined }) {
  return (
    <>
      <LivePerformance page={page} live={live} />
      <div className="row">
        <div className="col-md-7">
          <div className="row">
            <div className="col-md-12 divider">
              <h4 className="bordered">CPU</h4>
            </div>
            {page.cpus.map((cpu) => (
              <div className="col-md-6" key={cpu.name}>
                <div className="well bg-steel">
                  <div className="content-well">
                    <div className="hold-icon">
                      <CpuChart load={cpu.load} />
                    </div>
                    <section className="details" style={{ marginBottom: 0 }}>
                      <h4 className="color-white">
                        <i className="fas fa-tachometer-alt"></i> {cpu.name}
                      </h4>
                      <p>
                        <strong>Load: </strong>
                        {cpu.load}%
                      </p>
                      <p>
                        <strong>Current clock: </strong>
                        {cpu.current_clock}
                      </p>
                      <p>
                        <strong>Max clock: </strong>
                        {cpu.max_clock}
                      </p>
                      <p>
                        <strong>Temperature: </strong>
                        {cpu.temperature}&deg;C
                      </p>
                    </section>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="row">
            <div className="col-md-12 divider">
              <h4 className="bordered">Storage</h4>
            </div>
            {page.hdds.map((hdd) => (
              <div className="col-md-6" key={hdd.index}>
                <div className="well bg-steel">
                  <div className="content-well">
                    <div className="hold-icon">
                      <div className="title">{hdd.index}</div>
                      <i className="far fa-hdd"></i>
                    </div>
                    <section className="details" style={{ marginBottom: 5 }}>
                      <p>
                        <strong>Size: </strong>
                        {hdd.size}
                      </p>
                      <p>
                        <strong>Unallocated: </strong>
                        {hdd.unallocated}
                      </p>
                      <p>
                        <strong>S.M.A.R.T. status: </strong>
                        <SmartMark smart={hdd.smart} />
                      </p>
                      <p>
                        <strong>WMI Status: </strong>
                        <WmiMark ok={hdd.wmi_ok} />
                      </p>
                      <p>
                        <strong>Temperature: </strong>
                        {hdd.temperature ? <>{hdd.temperature}&deg;C</> : 'n/a'}
                      </p>
                    </section>
                  </div>
                  <div className="progress progress-xs progress-white progress-over-tile">
                    <div
                      className="progress-bar progress-bar-white progress-bar-warning"
                      data-transitiongoal={hdd.used}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      style={{ width: `${hdd.used}%` }}
                    ></div>
                  </div>
                  <label className="progress-label label-white"> Used space : {hdd.used}% </label>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="col-md-5">
          <SensorTable heading="VOLTAGES" unit="V" rows={page.voltages} />
          <SensorTable heading="FANS" unit="RPM" rows={page.fans} />
        </div>
      </div>
    </>
  )
}
